import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {
  COLUMN_TYPE,
  FileData,
  IMPORT_TYPE,
  OPTIONS,
  RegistryImportSettingsProps,
  SelectedSchemaIds,
  SpreadsheetColumn,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

export type GetRequiredColumnsErrorFunction = (
  columns: Array<SpreadsheetColumn>,
  options: {
    selectedSchemaIds: SelectedSchemaIds;
    registryImportSettings: RegistryImportSettingsProps;
    schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
    importType: IMPORT_TYPE;
  }
) => string | null;

export interface RequiredColumnsErrorState {
  options: {[key in OPTIONS]: boolean};
  registryImportSettings: RegistryImportSettingsProps;
  selectedSchemaIds: SelectedSchemaIds;
  dataTableKeyFields?: Array<COLUMN_TYPE>;
  importType: IMPORT_TYPE;
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
}

export interface ColumnErrorsBannerProps {
  fileData: FileData;
  getRequiredColumnsError: GetRequiredColumnsErrorFunction;
  importType: IMPORT_TYPE;
  requiredColumnsErrorState: RequiredColumnsErrorState;
  /** Callback to confirm that columns marked as IGNORE by Benchling's automatic guessing should remain ignored */
  onConfirmIgnore: () => void;
}

/**
 * Returns errors for column mapping on MapColumnNamesModalPane.
 */
export function getColumnErrors(
  fileData: FileData,
  getRequiredColumnsError: GetRequiredColumnsErrorFunction,
  requiredColumnsErrorState: RequiredColumnsErrorState
): Array<string> {
  // Display any errors with the uploaded spreadsheet if they exist.
  if (fileData.errorMessage) {
    return [fileData.errorMessage];
  }
  const {columns} = fileData;
  // Otherwise, do not display validation errors until the columns finish loading.
  if (columns.length === 0) {
    return [];
  }
  // Finally, return any column validation errors specific to flow.
  const error = getRequiredColumnsError(columns, requiredColumnsErrorState);
  if (error) {
    return [error];
  }

  const errors: Array<string> = [];
  // Raise errors for duplicate columns
  const duplicateColumnTypeError = SpreadsheetBulkImportUtil.getDuplicateColumnTypesError(columns);
  if (duplicateColumnTypeError) {
    errors.push(duplicateColumnTypeError);
  }

  // Raise errors for duplicate tag name columns
  const duplicateTagNameError = SpreadsheetBulkImportUtil.getDuplicateTagNameError(columns);
  if (duplicateTagNameError) {
    errors.push(duplicateTagNameError);
  }
  return errors;
}

export function getWarningMessage(column: SpreadsheetColumn, importType: IMPORT_TYPE): string | null {
  if (column.type === COLUMN_TYPE.CUSTOM_FIELD) {
    return (
      'Custom fields will not appear on all entities of this schema. ' +
      'Consider adding this field to the schema.'
    );
  }
  return null;
}

function UnconfirmedIgnoredColumnsWarning({
  numUnconfirmedIgnoredColumns,
  onConfirmIgnore,
}: {
  numUnconfirmedIgnoredColumns: number;
  onConfirmIgnore: () => void;
}): JSX.Element {
  function handleClick(e: React.MouseEvent<HTMLAnchorElement>): void {
    e.preventDefault();
    onConfirmIgnore();
  }
  return (
    <div>
      <strong>
        {`Unable to identify column types for ${numUnconfirmedIgnoredColumns} ${pluralize(numUnconfirmedIgnoredColumns, 'column')}. Select a type or `}
        <a href="#" onClick={handleClick}>
          confirm 'Ignore'
        </a>
        .
      </strong>
      <div className="u-mini-spacing-top">Data in ignored columns will not be imported into Benchling</div>
    </div>
  );
}

/**
 * Returns warnings for column mapping on MapColumnNamesModalPane.
 */
export function getColumnWarnings(
  fileData: FileData,
  importType: IMPORT_TYPE,
  onConfirmIgnore: () => void
): Array<string | React.ReactNode> {
  if (!fileData.columns) {
    return [];
  }

  const warnings: Array<string | React.ReactNode> = [
    ...new Set(
      fileData.columns
        .map((column) => getWarningMessage(column, importType))
        .flatMap((column) => (column != null ? [column] : []))
    ),
  ];

  // Check for unconfirmed ignored columns warning
  const unconfirmedIgnoredColumns = fileData.columns.filter((column) =>
    SpreadsheetBulkImportUtil.isUnconfirmedIgnoredColumn(column)
  );

  if (unconfirmedIgnoredColumns.length > 0) {
    warnings.push(
      <UnconfirmedIgnoredColumnsWarning
        numUnconfirmedIgnoredColumns={unconfirmedIgnoredColumns.length}
        onConfirmIgnore={onConfirmIgnore}
      />
    );
  }

  return warnings;
}

export default function ColumnErrorsBanner(props: ColumnErrorsBannerProps): JSX.Element | null {
  const {fileData, getRequiredColumnsError, importType, requiredColumnsErrorState, onConfirmIgnore} = props;
  const errors = getColumnErrors(fileData, getRequiredColumnsError, requiredColumnsErrorState);
  const warnings = getColumnWarnings(fileData, importType, onConfirmIgnore);

  if (!errors.length && !warnings.length) {
    return null;
  }

  return (
    <>
      {errors.map((error, index) => (
        <div key={index} className="u-small-padding-bottom">
          <InlineBanner bannerType={InlineBannerType.ERROR} supportingInfo={error} />
        </div>
      ))}
      {warnings.map((warning, index) => (
        <div key={index} className="u-small-padding-bottom">
          <InlineBanner bannerType={InlineBannerType.WARNING} supportingInfo={warning} />
        </div>
      ))}
    </>
  );
}
